<script>
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js";
import {ref, watch} from "vue";
import {useWorkerStore} from "@/stores/Worker/Worker.js";
import AdvancedWorkerFilter from "@/views/Worker/Components/WorkerFilter.vue";
import {storeToRefs} from "pinia";
import WorkerImage from "@/views/Worker/Components/WorkerImage.vue";
import axios from "axios";

export default {
    name: "Workers",
    components: {
        WorkerImage,
        AdvancedWorkerFilter,
    },
    setup() {
        const {
            loading,
            total,
        } = useSharedData();

        const {getBack, transformFilterValues, formatedDate} = useSharedMethod();

        const {$router, $i18n} = useTools();

        const workerStore = useWorkerStore();
        const securityStore = useSecurityStore();
        const {idControlProgram, idIssue, selectedModule} = storeToRefs(securityStore);

        const filter = ref({
            phase: "ACTIVE",
            programIds: idControlProgram.value && selectedModule.value && selectedModule.value === 'worker' ? [idControlProgram.value] : []
        });

        const headers = ref([
            {
                text: "...",
                align: "start",
                sortable: false,
                value: "action",
            },
        ]);

        const imageHeader = {
            text: "",
            align: "start",
            sortable: false,
            value: "photo",
            fixed: true,
        };

        if (securityStore.userContext.workerShowImage)
            headers.value.push(imageHeader);

        headers.value.push(
            {
                text: $i18n.t("common.code"),
                align: "start",
                sortable: true,
                value: "code",
                fixed: true,
            },
            {
                text: $i18n.t("common.lastname"),
                align: "start",
                sortable: true,
                value: "lastName",
                fixed: true,
            },
            {
                text: $i18n.t("common.firstname"),
                align: "start",
                sortable: true,
                value: "firstName",
            },
            {
                text: $i18n.t("module.occupation"),
                align: "start",
                sortable: true,
                value: "occupationName",
            },
            {
                text: $i18n.t("module.organizational_chart"),
                align: "start",
                sortable: true,
                value: "organizationalChartName",
            },
            {
                text: $i18n.t("common.enter_date"),
                align: "start",
                sortable: true,
                value: "realEnterDate",
            },
            {
                text: $i18n.t("common.grille"),
                align: "start",
                sortable: true,
                value: "occupationCategoryClasseName",
            },
            {
                text: $i18n.t("module.state"),
                align: "start",
                sortable: true,
                value: "stateName",
            }
        );

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: (item) => item.updateOption,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'workers.edit', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: (item) => item.deleteOption,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'workers.delete', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.deactivate"),
                icon: 'mdi-power-plug-off',
                color: 'grey',
                access: (item) => item.updateOption,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'workers.deactivate', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('worker.print_ats'),
                icon: 'mdi-file',
                color: 'blue darken-2',
                access: (item) => item.printOption,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'workers.ats',params: { id: item.id }})
                },
            },
            {
                title: $i18n.t("module.edms"),
                icon: 'mdi-folder',
                color: 'amber',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `workers.documents`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("module.template_application"),
                icon: 'mdi-text-box-multiple',
                color: 'teal',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({ name: 'workers.template_applications', params: { id: item.id}})
                },
            },
        ];

        const searchParams = ref({
            sortBy: "lastName",
            descending: false,
            page: 1,
            itemsPerPage: securityStore.userContext.workerRowsPerPage
        });

        function handleSearch(params) {
            loading.value = true;
            workerStore.setWorkers([]);
            total.value = 0;

            filter.value.calculateRung = false;
            filter.value.calculateIep = false;

            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;

            axios.get('/workers', {params: transformFilterValues(filter.value)}).then((response) => {
                workerStore.setWorkers(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }

        function findAll() {
            handleSearch(searchParams.value)
        }

        watch(() => filter.value, () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});

        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});

        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                if (key === 'phase') {
                    filter.value[key] = "ACTIVE";
                } else {
                    filter.value[key] = null;
                }
            });
            idControlProgram.value = null;
            idIssue.value = null;
        }

        function view(item) {
            $router.push({name: "workers.view", params: {id: item.id}});
        }

        function setWorkerShowImage(show) {
            if (securityStore.userContext.workerShowImage !== show) {
                securityStore.setWorkerShowImage(show);
                if (show) {
                    headers.value.splice(1, 0, imageHeader);
                } else {
                    headers.value.splice(1, 1);
                }
                window.localStorage.setItem("userContext", JSON.stringify(securityStore.userContext));
            }
        }

        const loadingExportExcel = ref(false);

        function exportExcel() {
            loadingExportExcel.value = true;
            filter.value.sortBy = searchParams.value.sortBy;
            filter.value.descending = searchParams.value.descending;
            axios.get(`/workers/exportExcel`, {
                responseType: 'blob',
                params: filter.value,
            }).then(response => {
                // Create a new Blob object using the response data of the file
                const fileURL = window.URL.createObjectURL(new Blob([response.data]));
                const fileLink = document.createElement('a');

                fileLink.href = fileURL;
                fileLink.setAttribute('download', 'employés.xlsx'); // Set the file name for download
                document.body.appendChild(fileLink);

                fileLink.click(); // Programmatically trigger the download

                // Clean up by revoking the Object URL and removing the link element
                window.URL.revokeObjectURL(fileURL);
                fileLink.remove();
            }).finally(() => {
                loadingExportExcel.value = false;
            });
        }


        return {
            workerStore,
            securityStore,
            headers,
            loading,
            total,
            filter,
            loadingExportExcel,
            actions,
            searchParams,
            formatedDate,
            setWorkerShowImage,
            exportExcel,
            clearFilter,
            findAll,
            getBack,
            view,
        };
    },
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.worker')"
            :total="total"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <AdvancedWorkerFilter v-model="filter"/>
            </template>

            <template v-slot:leftToolbar>
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            icon
                            v-bind="attrs"
                            v-on="on"
                        >
                            <v-icon>mdi-view-grid</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item @click="setWorkerShowImage(false)">
                            <v-list-item-icon>
                                <v-icon>mdi-view-list</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t('common.list') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="setWorkerShowImage(true)">
                            <v-list-item-icon>
                                <v-icon>mdi-image</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t('common.image') }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            color="blue darken-2"
                            depressed
                            small
                            class="ml-2 white--text"
                            v-bind="attrs"
                            v-on="on"
                        >
                            {{ $t("common.more_action") }}
                            <v-icon right>mdi-chevron-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item
                            v-if="securityStore.authorities['UPDATE_WORKER']"
                            @click="$router.push({name: 'workers.set_data_access'})"
                        >
                            <v-list-item-icon>
                                <v-icon>mdi-server-security</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>
                                {{ $t('worker.set_data_access') }}
                            </v-list-item-title>
                        </v-list-item>
                        <v-divider/>
                        <v-list-item
                            v-if="securityStore.authorities['UPDATE_PAY_SLIP']"
                            @click="$router.push({name: 'workers.add_pay_sections'})"
                        >
                            <v-list-item-icon>
                                <v-icon>mdi-bookshelf</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title> {{ $t('common.add_pay_sections') }}</v-list-item-title>
                        </v-list-item>
                        <v-divider/>
                        <v-list-item
                            v-if="securityStore.authorities['UPDATE_PAY_SLIP']"
                            @click="$router.push({name: 'workers.delete_pay_sections'})"
                        >
                            <v-list-item-icon>
                                <v-icon>mdi-delete</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title> {{ $t('common.delete_pay_sections') }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <w-btn-export
                    :loading="loadingExportExcel"
                    @press="exportExcel"
                />
                <w-btn-add
                    :show="securityStore.authorities['SAVE_WORKER']"
                    @press="$router.push({name: 'workers.add'})"
                />
            </template>

            <template v-slot:content>
                <w-server-data-table
                    v-model="searchParams"
                    :actions="actions"
                    :headers="headers"
                    :items="workerStore.workers"
                    :loading="loading"
                    :total="total"
                    root-path="workers"
                    module="WORKER"
                    @rowDbClick="view"
                >
                    <template v-slot:[`item.photo`]="{ item }">
                        <WorkerImage
                            :worker-id="item.id"
                            disable-card
                            readonly
                        />
                    </template>
                    <template v-slot:[`item.code`]="{ item }">

                        <w-chip
                            :text="item.code"
                            :icon="item.head ? 'mdi-account-supervisor-circle' : null"
                            icon-color="teal"
                        />
                    </template>
                    <template v-slot:[`item.lastName`]="{ item }">
                        <w-inline-text bold :text="item.lastName"/>
                    </template>
                    <template v-slot:[`item.firstName`]="{ item }">
                        <w-inline-text bold :text="item.firstName"/>
                    </template>
                    <template v-slot:[`item.occupationName`]="{ item }">
                        {{ item.occupationName }}
                    </template>
                    <template v-slot:[`item.organizationalChartName`]="{ item }">
                        {{ item.organizationalChartName }}
                    </template>
                    <template v-slot:[`item.realEnterDate`]="{ item }">
                        <w-inline-text :text="formatedDate(item.realEnterDate)"/>
                    </template>
                    <template v-slot:[`item.occupationCategoryClasseName`]="{ item }">
                        <w-inline-text :text="item.occupationCategoryFullName"/>
                    </template>
                    <template v-slot:[`item.stateName`]="{ item }">
                        <w-chip color="teal" :text="item.stateName"/>
                    </template>
                </w-server-data-table>
            </template>
        </w-app>

        <router-view/>
    </div>
</template>